// Setting this at the top to disable the empty anchor warning, since we can't do it inline
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react';
import { Col, Row, Tabs, Tab } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';
import { cloneDeep } from 'lodash';

import PageHeader from './common/PageHeader/PageHeader';
import CourseTile from './common/CourseTile/CourseTile';
import PageFooter from './common/PageFooter/PageFooter';

import { RootState } from '../ducks/store';
import { getMaintenanceMessage } from '../utils/functions';
import { showModal } from '../ducks/modal';
import { ModalTypes } from '../utils';

import './Dashboard.scss';

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, maintenance } = useSelector((state: RootState) => {
    const user = state.auth.authenticatedUser || {};
    const { maintenance } = state.platformData;
    return {
      user,
      maintenance,
    };
  });

  const { courseAssignments, notices } = user;
  const courses = user.courses as ICourse[];
  const [showExpiredCourses, setShowExpiredCourses] = useState(true);
  const activeCourses: ICourse[] = [];
  const expiredCourses: ICourse[] = [];
  courses?.forEach((course) => {
    const { _id } = course;
    const assignment = courseAssignments?.find((a) => a.courseId === _id);

    // If not ignoring expiration and courseExpiration exists, check date
    if (assignment?.expiration) {
      let date = new Date();
      if (assignment.expiration) {
        date = new Date(assignment.expiration);
      }

      const now = new Date();
      // If the date/time now is greater/equal to the expiration date,
      // add to expiration list
      if (now >= date) {
        expiredCourses.push(course);
      } else {
        activeCourses.push(course);
      }
    } else {
      activeCourses.push(course);
    }
  });

  const incompleteActiveCourses = activeCourses.filter(
    (course) => !course.progress || course.progress < 100,
  );
  const completedActiveCourses = activeCourses.filter((course) => course.progress === 100);

  const incompleteExpiredCourses = expiredCourses.filter(
    (course) => !course.progress || course.progress < 100,
  );
  const completedExpiredCourses = expiredCourses.filter((course) => course.progress === 100);

  // Generating strings for tab titles
  const numOfAllCourses = activeCourses.length + expiredCourses.length;
  const numOfIncompleteCourses = incompleteActiveCourses.length + incompleteExpiredCourses.length;
  const numOfCompletedCourses = completedActiveCourses.length + completedExpiredCourses.length;

  const allCoursesTitle = `All (${numOfAllCourses})`;
  const incompleteCoursesTitle = `Incomplete (${numOfIncompleteCourses})`;
  const completedCoursesTitle = `Complete (${numOfCompletedCourses})`;

  interface IRenderCourseSectionProps {
    courseList?: ICourse[];
    expired?: boolean;
    needsExtraSpaceAbove?: boolean;
  }
  const renderCourseSection = ({
    courseList,
    expired,
    needsExtraSpaceAbove,
  }: IRenderCourseSectionProps) => {
    // Do not render anything if this is an expired section and there are no courses in the list
    // (continue with rendering for 'active' section, even if there are no courses in the list)
    if ((!courseList || courseList.length < 1) && expired) {
      return;
    }
    const clonedCourseList = cloneDeep(courseList);
    const alphabetizedCourseList = clonedCourseList?.sort((a, b) =>
      (a.title || '').localeCompare(b.title || ''),
    );
    return (
      <>
        <h2
          onClick={() => (expired ? setShowExpiredCourses(!showExpiredCourses) : null)}
          className={classNames('courses-header', {
            'mt-65': needsExtraSpaceAbove,
            expired,
          })}
        >
          {expired ? (
            <span className="material-icons expired-courses-arrow mr-2">
              {showExpiredCourses ? 'expand_more' : 'chevron_right'}
            </span>
          ) : null}
          {expired ? 'Expired Courses' : 'Active Courses'}
        </h2>
        {(!expired || (expired && showExpiredCourses)) && (
          <>
            {expired && (
              // TODO - The use of 'a' tag here is not best practice. This is a quick fix for now
              // but should be replaced with a component that serves a similar purpose, like TextButton
              <div className="expired-courses-copy">
                Your access to the following courses has expired. To renew access,{' '}
                <a href="javascript:;" onClick={() => dispatch(showModal(ModalTypes.Feedback))}>
                  contact us
                </a>{' '}
                or an administrator at your organization.
              </div>
            )}
            {/* If there are courses to display, list them out. */}
            {alphabetizedCourseList && (
              <ul className="Dashboard__course-list">
                {alphabetizedCourseList.map((course) => (
                  <CourseTile
                    key={course._id}
                    course={course}
                    navigate={navigate}
                    expired={expired}
                    courseAssignments={courseAssignments}
                  />
                ))}
              </ul>
            )}

            {/* If the course list is empty, but this is the active course */}
            {/* section, display a message. */}
            {(!courseList || courseList.length === 0) && !expired && (
              <h6 className="w-100 text-left py-3 font-weight-bold">No active courses</h6>
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div className="Dashboard">
      <Helmet>
        <title>Dashboard - Praktio</title>
      </Helmet>
      <PageHeader pageName="Your Courses" />
      <div className="Dashboard-content">
        <Row className="ml-0 mr-0">
          {/* Main Course Content */}
          <Col className="order-1 order-lg-0 mr-lg-4">
            <Tabs
              defaultActiveKey="all"
              id="courses-tabs"
              className="mb-4 sticky-top Dashboard-tabs custom-tabs"
              transition={false}
            >
              <Tab eventKey="all" title={allCoursesTitle}>
                {renderCourseSection({ courseList: activeCourses })}
                {renderCourseSection({
                  courseList: expiredCourses,
                  expired: true,
                  needsExtraSpaceAbove: activeCourses.length > 0,
                })}
              </Tab>
              <Tab eventKey="incomplete" title={incompleteCoursesTitle}>
                {renderCourseSection({ courseList: incompleteActiveCourses })}
                {renderCourseSection({
                  courseList: incompleteExpiredCourses,
                  expired: true,
                  needsExtraSpaceAbove: incompleteActiveCourses.length > 0,
                })}
              </Tab>
              <Tab eventKey="complete" title={completedCoursesTitle}>
                {renderCourseSection({ courseList: completedActiveCourses })}
                {renderCourseSection({
                  courseList: completedExpiredCourses,
                  expired: true,
                  needsExtraSpaceAbove: completedActiveCourses.length > 0,
                })}
              </Tab>
            </Tabs>
          </Col>
          {/* User notifications */}
          {((notices && notices.length > 0) || !!maintenance) && (
            <Col xs={12} lg={3} className="order-0 order-lg-1 ml-lg-4">
              <Row>
                {!!maintenance && (
                  <Col
                    xs={12}
                    className="alert alert-warning notification d-flex notification"
                    role="alert"
                    aria-live="assertive"
                  >
                    <div className="icon d-flex justify-content-center align-items-center">
                      <span className="material-icons">warning</span>
                    </div>
                    <div>{getMaintenanceMessage(maintenance)}</div>
                  </Col>
                )}
                {notices?.map((notice, i) => (
                  <Col
                    key={`notice-${i}`}
                    xs={12}
                    className="alert alert-warning notification d-flex notification"
                    role="alert"
                    aria-live="assertive"
                  >
                    <div className="icon d-flex justify-content-center align-items-center">
                      <span className="material-icons">warning</span>
                    </div>
                    <div>{notice}</div>
                  </Col>
                ))}
              </Row>
            </Col>
          )}
        </Row>
      </div>
      <PageFooter />
    </div>
  );
};

export default Dashboard;
